import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const DistrictWiseLoanExcel = async (
    data,
    startDate,
    endDate,
    selectedDistrictNames = [],
    loginPlaceId
) => {
    if (!Array.isArray(data) || data.length === 0) {
        return;
    }

    const cleanedData = data.map(item => ({
        ...item,
        districtName: (item.districtName || "").trim()
    }));

    const districtDataMap = {};
    cleanedData.forEach(item => {
        if (item.districtName) {
            districtDataMap[item.districtName] = item;
        }
    });

    let districts;
    if (selectedDistrictNames && selectedDistrictNames.length > 0) {
        districts = selectedDistrictNames.map(name => String(name).trim()).filter(name => name);
    } else {
        districts = [...new Set(cleanedData.map(d => d.districtName))].filter(d => d).sort();
    }

    if (districts.length === 0) {
        return;
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Loan Management System';
    workbook.created = new Date();

    const sheet = workbook.addWorksheet("District Wise Loan Report");

    const metrics = [
        {
            label: "Loan Disbursement",
            countKey: "disbursement_count",
            amountKey: "disbursement_amount"
        },
        {
            label: "Amount to be Recovered",
            amountKey: "amount_to_be_recovered"
        },
        {
            label: "Amount Recovered",
            amountKey: "amount_recoverd"
        },
        {
            label: "Total Outstanding",
            countKey: "total_out_standing_count",
            amountKey: "total_out_standing_amount"
        },
        {
            label: "Past Due Loans",
            countKey: "pastdue_count",
            amountKey: "pastdue_amount"
        },
        {
            label: "Total Amount",
            amountKey: "amount"
        },
        {
            label: "Default Willful Loans",
            countKey: "defaultWillfulLoanCount",
            amountKey: "defaultWillfulLoanAmount"
        }
    ];

    const totalColumns = 1 + (districts.length * 2);

    const amountFormat = "#,##0.00";
    const countFormat = "0";
    let currentRow = 1;

    sheet.mergeCells(currentRow, 1, currentRow, totalColumns);
    const titleCell = sheet.getCell(currentRow, 1);
    titleCell.value = "District Wise Loan Report";
    titleCell.font = { bold: true, size: 18, color: { argb: "000080" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(currentRow).height = 35;
    currentRow++;

    sheet.mergeCells(currentRow, 1, currentRow, totalColumns);
    const periodCell = sheet.getCell(currentRow, 1);
    periodCell.value = `Period: ${startDate} to ${endDate}`;
    periodCell.font = { bold: true, size: 14 };
    periodCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(currentRow).height = 25;
    currentRow++;

    currentRow++;

    let colIndex = 2;

    districts.forEach(district => {
        sheet.mergeCells(currentRow, colIndex, currentRow, colIndex + 1);
        const districtHeaderCell = sheet.getCell(currentRow, colIndex);
        districtHeaderCell.value = district;
        districtHeaderCell.font = {
            bold: true,
            size: 12,
            color: { argb: "0000FF" }
        };
        districtHeaderCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "E6F3FF" }
        };
        districtHeaderCell.alignment = {
            horizontal: "center",
            vertical: "middle"
        };

        sheet.getCell(currentRow + 1, colIndex).value = "Count";
        sheet.getCell(currentRow + 1, colIndex + 1).value = "Amount (Rs.)";

        [0, 1].forEach(offset => {
            const cell = sheet.getCell(currentRow + 1, colIndex + offset);
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "D9E1F2" }
            };
        });

        colIndex += 2;
    });

    sheet.getCell(currentRow, 1).value = "Categories";
    sheet.getCell(currentRow, 1).font = { bold: true };
    sheet.getCell(currentRow, 1).alignment = { horizontal: "center", vertical: "middle" };
    sheet.getCell(currentRow, 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E6F3FF" }
    };

    sheet.getCell(currentRow + 1, 1).value = "";
    sheet.getCell(currentRow + 1, 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "D9E1F2" }
    };

    currentRow += 2;

    metrics.forEach(metric => {
        const categoryCell = sheet.getCell(currentRow, 1);
        categoryCell.value = metric.label;
        categoryCell.font = { bold: true };
        categoryCell.alignment = {
            horizontal: "left",
            vertical: "middle",
            indent: 2
        };

        let dataColIndex = 2;
        districts.forEach(district => {
            const districtData = districtDataMap[district] || {};

            if (metric.countKey) {
                const countValue = districtData[metric.countKey] || 0;
                const amountValue = districtData[metric.amountKey] || 0;

                const countCell = sheet.getCell(currentRow, dataColIndex);
                countCell.value = countValue;
                countCell.numFmt = countFormat;
                countCell.alignment = { horizontal: "right", vertical: "middle" };

                const amountCell = sheet.getCell(currentRow, dataColIndex + 1);
                amountCell.value = amountValue;
                amountCell.numFmt = amountFormat;
                amountCell.alignment = { horizontal: "right", vertical: "middle" };
            } else {
                const amountValue = districtData[metric.amountKey] || 0;

                sheet.mergeCells(currentRow, dataColIndex, currentRow, dataColIndex + 1);
                const mergedCell = sheet.getCell(currentRow, dataColIndex);
                mergedCell.value = amountValue;
                mergedCell.numFmt = amountFormat;
                mergedCell.alignment = { horizontal: "right", vertical: "middle" };
            }

            dataColIndex += 2;
        });

        currentRow++;
    });

    sheet.getCell(currentRow, 1).value = "GRAND TOTAL";
    sheet.getCell(currentRow, 1).font = { bold: true };
    sheet.getCell(currentRow, 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFCC" }
    };

    let totalColIndex = 2;
    districts.forEach(district => {
        const districtData = districtDataMap[district] || {};

        let totalCount = 0;
        let totalAmount = 0;

        metrics.forEach(metric => {
            if (metric.countKey) {
                totalCount += Number(districtData[metric.countKey] || 0);
            }
            if (metric.amountKey) {
                totalAmount += Number(districtData[metric.amountKey] || 0);
            }
        });

        const countCell = sheet.getCell(currentRow, totalColIndex);
        countCell.value = totalCount;
        countCell.numFmt = countFormat;
        countCell.font = { bold: true };
        countCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFCC" }
        };
        countCell.alignment = { horizontal: "right", vertical: "middle" };

        const amountCell = sheet.getCell(currentRow, totalColIndex + 1);
        amountCell.value = totalAmount;
        amountCell.numFmt = amountFormat;
        amountCell.font = { bold: true };
        amountCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFCC" }
        };
        amountCell.alignment = { horizontal: "right", vertical: "middle" };

        totalColIndex += 2;
    });

    for (let row = 1; row <= currentRow; row++) {
        for (let col = 1; col <= totalColumns; col++) {
            const cell = sheet.getCell(row, col);
            if (!cell.border) {
                cell.border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" }
                };
            }
        }
    }

    sheet.getColumn(1).width = 35;
    for (let col = 2; col <= totalColumns; col++) {
        sheet.getColumn(col).width = 16;
    }

    sheet.getRow(1).height = 35;
    sheet.getRow(2).height = 25;
    for (let row = 4; row <= currentRow; row++) {
        sheet.getRow(row).height = 24;
    }

    sheet.views = [{ state: "frozen", xSplit: 1, ySplit: 4 }];

    try {
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const fileName = `District_Wise_Loan_Report_${startDate.replace(/\//g, "-")}_to_${endDate.replace(/\//g, "-")}.xlsx`;
        saveAs(blob, fileName);
    } catch (error) {
        // Silent error
    }
};

export default DistrictWiseLoanExcel;